import { CurveFactory, CurveFactoryLineOnly, curveCatmullRom, curveLinear, line } from "d3";

import { getPointsGrouped } from "../components/frame-stencil-drawing/utils/get-points-grouped";
import { Action, MidPoint, Point, PointType } from "../types";
import { bezierPathsForTouchableAnchorPointPaths } from "./bezier-points";

export type GetActionPathProps = Pick<Action, "pos" | "midPoints" | "endPos">;

export const getActionPath = (action: GetActionPathProps) => getActionPathSegments(action).join(" ");

export const getActionPathSegments = (action: GetActionPathProps) => {
  const { pos, midPoints, endPos } = action;

  const lineData: MidPoint[] = [
    { ...pos, pointType: PointType.START },
    ...midPoints,
    { ...endPos, pointType: PointType.END },
  ];

  const groupedLine = getPointsGrouped(lineData);

  const linePath = (curve: CurveFactory | CurveFactoryLineOnly) =>
    line<Point>()
      .x((d) => d.x)
      .y((d) => d.y)
      .curve(curve);

  return groupedLine.map<string>((group) => {
    switch (group.pointType) {
      case PointType.ANGLE:
        return `${linePath(curveLinear)(group.points)}`;

      case PointType.CURVE: {
        const { points } = group;

        const bezierPoints = bezierPathsForTouchableAnchorPointPaths([points]).flat();
        switch (bezierPoints.length) {
          case 2:
            return `${linePath(curveLinear)(bezierPoints)}`;
          case 3:
            return `M ${bezierPoints[0]!.x} ${bezierPoints[0]!.y} Q${bezierPoints
              .slice(1)
              .map((p) => `${p.x} ${p.y}`)
              .join(",")}`;
          case 4:
            return `M ${bezierPoints[0]!.x} ${bezierPoints[0]!.y} C${bezierPoints
              .slice(1)
              .map((p) => `${p.x} ${p.y}`)
              .join(",")}`;
          default:
            return `${linePath(curveCatmullRom.alpha(1))(points)}`;
        }
      }

      default:
        return `${linePath(curveLinear)(group.points)}`;
    }
  });
};

export const getPath = (pathString: string) => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

  path.setAttribute("d", pathString);
  svg.appendChild(path);

  return path;
};

export const getPathLength = (pathString: string) => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

  path.setAttribute("d", pathString);
  svg.appendChild(path);

  document.body.appendChild(svg);

  const pathLength = path.getTotalLength();
  document.body.removeChild(svg);

  return pathLength;
};
